/**
 * @prettier
 * @flow
 */
import apisauce from 'apisauce';
import type { CancelPaymentLinkRequest } from 'src/api/types/CancelPaymentLinkRequest';
import type { CancelPaymentLinkResponse } from 'src/api/types/CancelPaymentLinkResponse';
import type { CreatePaymentLinkRequest } from 'src/api/types/CreatePaymentLinkRequest';
import type { CreatePaymentLinkResponse } from 'src/api/types/CreatePaymentLinkResponse';
import type { GetAppContextRequest } from 'src/api/types/GetAppContextRequest';
import type { GetAppContextResponse } from 'src/api/types/GetAppContextResponse';
import type { GetPaymentLinkRequest } from 'src/api/types/GetPaymentLinkRequest';
import type { GetPaymentLinkResponse } from 'src/api/types/GetPaymentLinkResponse';
import type { GetPaymentLinksRequest } from 'src/api/types/GetPaymentLinksRequest';
import type { GetPaymentLinksResponse } from 'src/api/types/GetPaymentLinksResponse';
import type { SignInUserRequest } from 'src/api/types/SignInUserRequest';
import type { SignInUserResponse } from 'src/api/types/SignInUserResponse';
import { appVersion } from 'src/config/appVersion';
import { store } from 'src/config/store';
import { Environments, type Environment } from 'src/constants/Environment';
import { envENVIRONMENT } from 'src/env/envENVIRONMENT';
import { getAccessTokenInLocalStorage } from 'src/localStorage/getAccessTokenInLocalStorage';
import { getRefreshTokenInLocalStorage } from 'src/localStorage/getRefreshTokenInLocalStorage';
import { setAccessTokenInLocalStorage } from 'src/localStorage/setAccessTokenInLocalStorage';
import { actions } from 'src/reducers';
import { translate } from 'src/utils/translate/translate';

function createPaymentLinkApi(environment: Environment) {
    let baseURL;
    const params = {};
    switch (environment) {
        case Environments.LOCALHOST:
            baseURL = 'http://localhost:7071/api/paymentlinksmanager';
            break;
        case Environments.TEST:
            baseURL = 'https://apipaymentlink-test.azurewebsites.net/api/paymentlinksmanager';
            params.code = 'Jbvq1LNMSSRovjxONLP6I7k1Ojn/GKNP4C8irnLCG6DtZTZiFOl74Q==';
            break;
        case Environments.PROD:
            baseURL = 'https://apipaymentlink.azurewebsites.net/api/paymentlinksmanager';
            params.code = 'MKzT9r4XvibA5G3HbC14dpvzCC9erFYLMxSc/FfoPySHoQbkGErIgQ==';
            break;
        default:
            throw new Error(`Invalid argument environment=${environment}`);
    }

    const api = apisauce.create({
        baseURL,
        headers: {
            'Content-Type': 'application/json',
        },
        timeout: 60000, // 60 second timeout
    });

    api.addRequestTransform(request => {
        const accessToken = getAccessTokenInLocalStorage();
        request.headers.Authorization = accessToken ? `Bearer ${accessToken}` : undefined;
    });

    api.addAsyncResponseTransform(async response => {
        if (response.config.url === '/signInUser') return response; // skip refresh token logic for sign in api
        if (response.config.url === '/refreshAccessToken') return response; // skip refresh token logic if already calling refresh token api
        if (response.status === 401) {
            response.config._retry = true;
            // accessToken expired, try to refresh it
            const refreshAccessTokenResponse = await api.any({
                method: 'post',
                url: '/refreshAccessToken',
                data: { refreshToken: getRefreshTokenInLocalStorage() },
            });
            if (refreshAccessTokenResponse.ok) {
                const accessToken = refreshAccessTokenResponse.data.accessToken;
                setAccessTokenInLocalStorage(accessToken);
                response.config.headers.Authorization = `Bearer ${accessToken}`;
                // Retry the original api call
                const retriedResponse = await api.any({
                    ...response.config,
                    data: response.config.data ? JSON.parse(response.config.data) : undefined, // repost with data as object to solve issue with calling addRequestTransform below
                });
                Object.keys(response).forEach(key => {
                    response[key] = retriedResponse[key];
                });
                return;
            } else {
                // Refreshing accessToken failed
                store.dispatch(actions.signOutUser({ signedOutReason: `${translate('Singed out since have been signed in for too long.')}\n${translate('Please sign in again.')}}` }));
                return;
            }
        }
    });

    if (process.env.NODE_ENV === 'development') {
        api.addRequestTransform(request => {
            const other = {};
            // if (request.headers) {
            //     other.headers = request.headers;
            // }
            if (request.params) {
                other.params = request.params;
            }
            if (request.data) {
                other.data = request.data;
            }
            console.log(`${request.method.toUpperCase()} ${baseURL}${request.url}`, other);
        });
        api.addMonitor(response => {
            console.log(`${response.status} <- ${response.config.url}`, response.data);
        });
    }

    api.addRequestTransform(request => {
        if (REQUEST_METHODS_WITH_PARAMS.includes(request.method)) {
            if (!request.params) request.params = {};
            request.params.appVersion = appVersion;
            request.params.code = params.code;
        } else {
            if (!request.data) request.data = {};
            if (!request.params) request.params = {};
            request.data.appVersion = appVersion;
            request.params.code = params.code;
        }
    });

    return {
        config: api,
        signInUser: (request: SignInUserRequest): {| ...ApiSauceResponse, data: SignInUserResponse |} => api.post('/signInUser', request),
        getAppContext: (request: GetAppContextRequest): {| ...ApiSauceResponse, data: GetAppContextResponse |} => api.post('/getAppContext', request),
        getPaymentLinks: (request: GetPaymentLinksRequest): {| ...ApiSauceResponse, data: GetPaymentLinksResponse |} => api.post('/getPaymentLinks', request),
        getPaymentLink: (request: GetPaymentLinkRequest): {| ...ApiSauceResponse, data: GetPaymentLinkResponse |} => api.post('/getPaymentLink', request),
        createPaymentLink: (request: CreatePaymentLinkRequest): {| ...ApiSauceResponse, data: CreatePaymentLinkResponse |} => api.post('/createPaymentLink', request),
        cancelPaymentLink: (request: CancelPaymentLinkRequest): {| ...ApiSauceResponse, data: CancelPaymentLinkResponse |} => api.post('/cancelPaymentLink', request),
        cancelPaymentLinkWithPayment: (request: CancelPaymentLinkRequest): {| ...ApiSauceResponse, data: CancelPaymentLinkResponse |} => api.post('/cancelPaymentLinkWithPayment', request),
    };
}

export const paymentLinkApi = createPaymentLinkApi(envENVIRONMENT());

const REQUEST_METHODS_WITH_PARAMS = ['get', 'head', 'delete', 'link', 'unlink'];

type ApiSauceResponse = {|
    ok: boolean,
    problem: 'NETWORK',
    status: 401,
|};
